import { FC, Suspense, useEffect, useLayoutEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Helmet from '@/components/meta/Helmet';
import ErrorTemplate from '@/components/templates/Error';
import MultiraceResultsTemplate from '@/components/templates/Results/Multirace';
import SingleraceResultsTemplate from '@/components/templates/Results/Singlerace';
import { DATE_PARAM_KEY } from '@/config/constants/query';
import { useSetBreadcrumbContext } from '@/contexts/breadcrumb';
import { useLayoutContext } from '@/contexts/layout';
import { useUserContext } from '@/contexts/user';
import {
  MultiraceProgram,
  RaceProgram,
  useGetEntryRacerNoteCountsLazyQuery,
  useGetMultiraceProgramsLazyQuery,
  useGetRaceProgramsLazyQuery,
} from '@/lib/apollo/types/gen/graphql';
import { dayjst } from '@/lib/dayjst';
import { RaceType } from '@/modelTypes';

const RACE_TYPE_PARAM_KEY = 'type';

const ResultsPage: FC = () => {
  const { setLayoutStyles } = useLayoutContext();
  const setBreadcrumbs = useSetBreadcrumbContext();

  useLayoutEffect(() => {
    setLayoutStyles();
  }, []);

  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const { isMember } = useUserContext();

  const paramDate = queryParams.get(DATE_PARAM_KEY);
  const targetDate = paramDate ? dayjst(paramDate) : dayjst();

  const paramType = queryParams.get(RACE_TYPE_PARAM_KEY);
  const [raceType, setRaceType] = useState<RaceType>(
    Object.values(RaceType).includes(paramType as RaceType)
      ? (paramType as RaceType)
      : RaceType.Singlerace,
  );

  const [loadRacePrograms, raceProgramsQuery] = useGetRaceProgramsLazyQuery();
  const [loadMultiracePrograms, multiraceProgramsQuery] =
    useGetMultiraceProgramsLazyQuery();
  const [loadRacerNoteCounts, racerNoteCountsQuery] =
    useGetEntryRacerNoteCountsLazyQuery();

  useEffect(() => {
    const variables = {
      input: {
        heldDate: targetDate,
      },
    };
    if (raceType === RaceType.Singlerace) {
      loadRacePrograms({
        variables,
      });
    }
    if (raceType === RaceType.Multirace) {
      loadMultiracePrograms({
        variables,
      });
    }
    if (isMember) {
      loadRacerNoteCounts({
        variables: {
          heldDate: targetDate,
        },
      });
    }

    setBreadcrumbs([
      {
        name: `レース結果一覧（${
          raceType === RaceType.Singlerace ? '競輪' : 'チャリロト・Dokanto!'
        }）`,
        to: `/results?${RACE_TYPE_PARAM_KEY}=${raceType}`,
      },
      {
        name: targetDate.format('YYYY年M月D日'),
        to: '',
      },
    ]);
  }, [paramDate, raceType]);

  const getCommentCount = (raceId: string) => {
    if (isMember) {
      if (racerNoteCountsQuery.data) {
        const racerNoteCount =
          racerNoteCountsQuery.data.entryRacerNoteCounts.find(
            r => r.raceID === raceId,
          );
        if (racerNoteCount) {
          return racerNoteCount.count !== 0;
        }
      }
    }
    return false;
  };

  useEffect(() => {
    queryParams.set(RACE_TYPE_PARAM_KEY, raceType);
    navigate(`${location.pathname}?${queryParams.toString()}`, {
      replace: true,
    });
  }, [raceType]);

  const title =
    raceType === RaceType.Singlerace
      ? `競輪 ${targetDate.format('YYYY年M月D日')}全レース結果`
      : `チャリロト・Dokanto！（${targetDate.format('YYYY年M月D日')}）結果一覧`;

  const description =
    raceType === RaceType.Singlerace
      ? `競輪 ${targetDate.format('YYYY年M月D日')}のレース結果です。`
      : `競輪 ${targetDate.format(
          'YYYY年M月D日',
        )}の全チャリロト・Dokanto!結果です。`;

  return (
    <>
      <Helmet
        meta={{
          title,
          description: `${description}%s`,
          keywords: `${title},%s`,
        }}
      />
      <Suspense fallback={<></>}>
        {raceType === RaceType.Singlerace && (
          <>
            {raceProgramsQuery.error ? (
              <ErrorTemplate apolloError={raceProgramsQuery.error} />
            ) : (
              <SingleraceResultsTemplate
                date={targetDate}
                raceType={raceType}
                setRaceType={setRaceType}
                racePrograms={
                  (raceProgramsQuery.data?.racePrograms || []) as RaceProgram[]
                }
                loading={raceProgramsQuery.loading}
                getCommentCount={getCommentCount}
              />
            )}
          </>
        )}
        {raceType === RaceType.Multirace && (
          <>
            {multiraceProgramsQuery.error ? (
              <ErrorTemplate apolloError={multiraceProgramsQuery.error} />
            ) : (
              <MultiraceResultsTemplate
                date={targetDate}
                raceType={raceType}
                setRaceType={setRaceType}
                multiracePrograms={
                  (multiraceProgramsQuery.data?.multiracePrograms ||
                    []) as MultiraceProgram[]
                }
                loading={multiraceProgramsQuery.loading}
              />
            )}
          </>
        )}
      </Suspense>
    </>
  );
};
export default ResultsPage;
